<template>
  <div class="candidates-page container py-5">
    <header class="head">
      <h2 v-tx="'CandidatesTitle'">{{ tx('CandidatesTitle') }}</h2>
      <p class="muted" v-tx="'CandidatesSubtitle'">{{ tx('CandidatesSubtitle') }}</p>
    </header>

    <div v-if="blocked" class="banner blocked" v-tx="'CandidatesBlocked'">
      {{ tx('CandidatesBlocked') }}
    </div>

    <div v-else>
      <!-- filterlər -->
      <div class="filters">
        <select v-if="filterFlags.profession" v-model="filter.professionId" @change="applyFilter">
          <option :value="null">{{ tx('CandidatesFilterProfessionAll') }}</option>
          <option v-for="p in professions" :key="p.id" :value="p.id">{{ p.name }}</option>
        </select>
        <select v-if="filterFlags.experience" v-model="filter.experienceRange" @change="applyFilter">
          <option value="">{{ tx('CandidatesFilterExperienceAll') }}</option>
          <option value="0-1">{{ tx('CandidatesExp01') }}</option>
          <option value="1-3">{{ tx('CandidatesExp13') }}</option>
          <option value="3+">{{ tx('CandidatesExp3Plus') }}</option>
        </select>
        <input v-if="filterFlags.language" type="text" v-model="filter.language" @keyup.enter="applyFilter" :placeholder="tx('CandidatesFilterLanguagePlaceholder')" />
        <select v-if="filterFlags.certificate" v-model="filter.hasCertificate" @change="applyFilter">
          <option :value="null">{{ tx('CandidatesFilterCertAny') }}</option>
          <option :value="true">{{ tx('CandidatesYes') }}</option>
          <option :value="false">{{ tx('CandidatesNo') }}</option>
        </select>
        <button type="button" class="primary" @click="applyFilter" v-tx="'CandidatesApplyBtn'">{{ tx('CandidatesApplyBtn') }}</button>
        <button type="button" class="ghost" @click="resetFilter" v-tx="'CandidatesResetBtn'">{{ tx('CandidatesResetBtn') }}</button>
      </div>

      <div v-if="loading" class="status" v-tx="'CandidatesLoading'">{{ tx('CandidatesLoading') }}</div>
      <div v-else-if="!items.length" class="status" v-tx="'CandidatesEmpty'">{{ tx('CandidatesEmpty') }}</div>

      <div v-else class="grid">
        <article v-for="n in items" :key="n.id" class="card" @click="openDetail(n.id)">
          <header class="card-head">
            <div class="avatar" :style="{ background: avatarColor(n) }">
              <span>{{ initials(n) }}</span>
            </div>
            <div class="identity">
              <h3>{{ n.name }} {{ n.surname }}</h3>
              <p class="meta" v-if="(cardFlags.age && n.ageYears) || (cardFlags.citizenship && n.citizenship)">
                <span v-if="cardFlags.age && n.ageYears" v-tx="'CandidatesAgeSuffix'">{{ n.ageYears }} {{ tx('CandidatesAgeSuffix') }}</span>
                <span v-if="cardFlags.age && n.ageYears && cardFlags.citizenship && n.citizenship"> – </span>
                <span v-if="cardFlags.citizenship && n.citizenship">{{ n.citizenship }}</span>
              </p>
            </div>
          </header>

          <div class="chips">
            <span class="chip chip--navy" v-if="cardFlags.experience && n.totalExperienceYears != null" v-tx="'CandidatesExperienceSuffix'">{{ n.totalExperienceYears }} {{ tx('CandidatesExperienceSuffix') }}</span>
            <span class="chip chip--green" v-if="cardFlags.certificate && n.hasCertificate" v-tx="'CandidatesCertificate'">{{ tx('CandidatesCertificate') }}</span>
            <span class="chip chip--orange" v-if="cardFlags.applied && n.hasApplied" v-tx="'CandidatesApplied'">{{ tx('CandidatesApplied') }}</span>
          </div>

          <div class="tags" v-if="cardFlags.specialties || cardFlags.languages">
            <template v-if="cardFlags.specialties">
              <span v-for="s in n.specialties" :key="'s-' + s" class="tag tag--filled">{{ s }}</span>
            </template>
            <template v-if="cardFlags.languages">
              <span v-for="l in n.languages" :key="'l-' + l" class="tag tag--outline">{{ l }}</span>
            </template>
          </div>

          <button type="button" class="apply-btn" @click="onApply(n, $event)" v-tx="'CandidatesCardApply'">
            {{ tx('CandidatesCardApply') }}
          </button>
        </article>
      </div>

      <nav v-if="paging && paging.totalPages > 1" class="pager">
        <button type="button" :disabled="pageNumber === 1" @click="goPage(pageNumber - 1)">‹</button>
        <button v-for="p in pageRange" :key="p" :class="{ active: p === pageNumber }" @click="goPage(p)">{{ p }}</button>
        <button type="button" :disabled="pageNumber === paging.totalPages" @click="goPage(pageNumber + 1)">›</button>
        <span class="total" v-tx="'CandidatesTotal'">{{ tx('CandidatesTotal') }} {{ paging.totalItems }}</span>
      </nav>
    </div>
  </div>
</template>

<script>
import axios from "axios";

const FALLBACK = {
  az: {
    CandidatesTitle: "Namizədlər",
    CandidatesSubtitle: "Təsdiqlənmiş tibb bacılarının CV bazası.",
    CandidatesBlocked: "⚠ Profiliniz admin tərəfindən bloklanıb. CV bazasına müraciətiniz dayandırılıb. Lütfən admin ilə əlaqə saxlayın.",
    CandidatesFilterProfessionAll: "İxtisas (hamısı)",
    CandidatesFilterExperienceAll: "Təcrübə (hamısı)",
    CandidatesExp01: "0–1 il",
    CandidatesExp13: "1–3 il",
    CandidatesExp3Plus: "3+ il",
    CandidatesFilterLanguagePlaceholder: "Dil (məs: English)",
    CandidatesFilterCertAny: "Sertifikat (fərq etməz)",
    CandidatesYes: "Var",
    CandidatesNo: "Yoxdur",
    CandidatesApplyBtn: "Tətbiq",
    CandidatesResetBtn: "Sıfırla",
    CandidatesLoading: "Yüklənir…",
    CandidatesEmpty: "Filterə uyğun namizəd tapılmadı.",
    CandidatesAgeSuffix: "yaş",
    CandidatesExperienceSuffix: "il təcrübə",
    CandidatesCertificate: "Sertifikat",
    CandidatesApplied: "Müraciət edilib",
    CandidatesCardApply: "Müraciət et",
    CandidatesTotal: "Cəmi:",
  },
  de: {
    CandidatesTitle: "Kandidaten",
    CandidatesSubtitle: "Datenbank der verifizierten Pflegekräfte.",
    CandidatesBlocked: "⚠ Ihr Profil wurde vom Administrator gesperrt. Der Zugriff auf die CV-Datenbank ist eingestellt. Bitte kontaktieren Sie den Administrator.",
    CandidatesFilterProfessionAll: "Fachgebiet (alle)",
    CandidatesFilterExperienceAll: "Erfahrung (alle)",
    CandidatesExp01: "0–1 Jahre",
    CandidatesExp13: "1–3 Jahre",
    CandidatesExp3Plus: "3+ Jahre",
    CandidatesFilterLanguagePlaceholder: "Sprache (z. B. English)",
    CandidatesFilterCertAny: "Zertifikat (egal)",
    CandidatesYes: "Ja",
    CandidatesNo: "Nein",
    CandidatesApplyBtn: "Anwenden",
    CandidatesResetBtn: "Zurücksetzen",
    CandidatesLoading: "Wird geladen…",
    CandidatesEmpty: "Keine passenden Kandidaten gefunden.",
    CandidatesAgeSuffix: "Jahre",
    CandidatesExperienceSuffix: "Jahre Erfahrung",
    CandidatesCertificate: "Zertifikat",
    CandidatesApplied: "Beworben",
    CandidatesCardApply: "Bewerben",
    CandidatesTotal: "Gesamt:",
  },
  en: {
    CandidatesTitle: "Candidates",
    CandidatesSubtitle: "Database of verified nurses.",
    CandidatesBlocked: "⚠ Your profile has been blocked by the admin. Access to the CV database is suspended. Please contact the admin.",
    CandidatesFilterProfessionAll: "Specialty (all)",
    CandidatesFilterExperienceAll: "Experience (all)",
    CandidatesExp01: "0–1 years",
    CandidatesExp13: "1–3 years",
    CandidatesExp3Plus: "3+ years",
    CandidatesFilterLanguagePlaceholder: "Language (e.g. English)",
    CandidatesFilterCertAny: "Certificate (any)",
    CandidatesYes: "Yes",
    CandidatesNo: "No",
    CandidatesApplyBtn: "Apply",
    CandidatesResetBtn: "Reset",
    CandidatesLoading: "Loading…",
    CandidatesEmpty: "No candidates match the filter.",
    CandidatesAgeSuffix: "yrs",
    CandidatesExperienceSuffix: "yrs experience",
    CandidatesCertificate: "Certificate",
    CandidatesApplied: "Applied",
    CandidatesCardApply: "Apply",
    CandidatesTotal: "Total:",
  },
  ru: {
    CandidatesTitle: "Кандидаты",
    CandidatesSubtitle: "База резюме проверенных медсестёр.",
    CandidatesBlocked: "⚠ Ваш профиль заблокирован администратором. Доступ к базе резюме приостановлен. Пожалуйста, свяжитесь с администратором.",
    CandidatesFilterProfessionAll: "Специальность (все)",
    CandidatesFilterExperienceAll: "Опыт (все)",
    CandidatesExp01: "0–1 лет",
    CandidatesExp13: "1–3 лет",
    CandidatesExp3Plus: "3+ лет",
    CandidatesFilterLanguagePlaceholder: "Язык (напр. English)",
    CandidatesFilterCertAny: "Сертификат (неважно)",
    CandidatesYes: "Есть",
    CandidatesNo: "Нет",
    CandidatesApplyBtn: "Применить",
    CandidatesResetBtn: "Сбросить",
    CandidatesLoading: "Загрузка…",
    CandidatesEmpty: "Кандидаты по фильтру не найдены.",
    CandidatesAgeSuffix: "лет",
    CandidatesExperienceSuffix: "лет опыта",
    CandidatesCertificate: "Сертификат",
    CandidatesApplied: "Заявка отправлена",
    CandidatesCardApply: "Откликнуться",
    CandidatesTotal: "Всего:",
  },
  tr: {
    CandidatesTitle: "Adaylar",
    CandidatesSubtitle: "Onaylanmış hemşirelerin CV veritabanı.",
    CandidatesBlocked: "⚠ Profiliniz yönetici tarafından engellendi. CV veritabanına erişim durduruldu. Lütfen yönetici ile iletişime geçin.",
    CandidatesFilterProfessionAll: "Uzmanlık (hepsi)",
    CandidatesFilterExperienceAll: "Deneyim (hepsi)",
    CandidatesExp01: "0–1 yıl",
    CandidatesExp13: "1–3 yıl",
    CandidatesExp3Plus: "3+ yıl",
    CandidatesFilterLanguagePlaceholder: "Dil (örn: English)",
    CandidatesFilterCertAny: "Sertifika (farketmez)",
    CandidatesYes: "Var",
    CandidatesNo: "Yok",
    CandidatesApplyBtn: "Uygula",
    CandidatesResetBtn: "Sıfırla",
    CandidatesLoading: "Yükleniyor…",
    CandidatesEmpty: "Filtreye uygun aday bulunamadı.",
    CandidatesAgeSuffix: "yaş",
    CandidatesExperienceSuffix: "yıl deneyim",
    CandidatesCertificate: "Sertifika",
    CandidatesApplied: "Başvuruldu",
    CandidatesCardApply: "Başvur",
    CandidatesTotal: "Toplam:",
  },
};

export default {
  name: "CandidatesPage",
  data() {
    return {
      items: [],
      paging: null,
      pageNumber: 1,
      pageSize: 12,
      filter: { professionId: null, experienceRange: "", language: "", hasCertificate: null },
      loading: true,
      blocked: false,
    };
  },
  computed: {
    professions() { return this.$store.state.professions || []; },
    filterFlags() { return this.$store.getters["featureSettings/candidatesFilters"]; },
    cardFlags() { return this.$store.getters["featureSettings/candidatesCard"]; },
    pageRange() {
      const total = this.paging?.totalPages || 1;
      const cur = this.pageNumber;
      const max = 7;
      if (total <= max) return Array.from({ length: total }, (_, i) => i + 1);
      const half = Math.floor(max / 2);
      let start = Math.max(1, cur - half);
      let end = Math.min(total, start + max - 1);
      start = Math.max(1, end - max + 1);
      return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    },
  },
  mounted() {
    if (!this.professions.length) this.$store.dispatch("getProfessions");
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        const params = { PageNumber: this.pageNumber, PageSize: this.pageSize };
        if (this.filter.professionId) params.professionId = this.filter.professionId;
        if (this.filter.experienceRange) params.experienceRange = this.filter.experienceRange;
        if (this.filter.language?.trim()) params.language = this.filter.language.trim();
        if (this.filter.hasCertificate !== null) params.hasCertificate = this.filter.hasCertificate;

        const res = await axios.get(`/clinic/nurses`, { params });
        this.items = res.data?.data?.items || [];
        this.paging = res.data?.data?.paging || null;
      } catch (err) {
        if (err?.response?.status === 403) {
          this.blocked = true;
        }
      } finally {
        this.loading = false;
      }
    },
    applyFilter() { this.pageNumber = 1; this.load(); },
    resetFilter() {
      this.filter = { professionId: null, experienceRange: "", language: "", hasCertificate: null };
      this.pageNumber = 1; this.load();
    },
    goPage(p) {
      const total = this.paging?.totalPages || 1;
      if (p < 1 || p > total || p === this.pageNumber) return;
      this.pageNumber = p; this.load();
      window.scrollTo(0, 0);
    },
    openDetail(id) { this.$router.push({ name: "candidate-detail", params: { id } }); },
    avatarColor(n) {
      const palette = ["#132B59", "#894FE5", "#0FA5C6", "#10B881", "#F39C12", "#EF4444"];
      const idx = (Number(n.id) || 0) % palette.length;
      return palette[idx];
    },
    onApply(n, ev) {
      if (ev) ev.stopPropagation();
      // TODO: sürətli müraciət modalı (dizayn sonra). Hələlik detal səhifəsi.
      this.openDetail(n.id);
    },
    tx(key) {
      const v = this.$store.state.statics?.[key];
      if (v && String(v).trim()) return v;
      const lang = this.$store.state.lang || "az";
      return (FALLBACK[lang] || FALLBACK.az)[key] || "";
    },
    initials(n) {
      const a = (n.name || "").charAt(0).toUpperCase();
      const b = (n.surname || "").charAt(0).toUpperCase();
      return (a + b) || "?";
    },
  },
};
</script>

<style lang="scss" scoped>
.candidates-page { min-height: 60vh; }
.head { margin-bottom: 1.5rem;
  h2 { color: #1e3c72; margin: 0 0 0.25rem; }
  .muted { color: #64748b; margin: 0; font-size: 0.9rem; }
}
.banner.blocked { background: #fdecea; color: #c0392b; padding: 1rem; border-radius: 8px; border: 1px solid #f1c2bd; font-size: 0.9rem; }

.filters { display: flex; gap: 12px; margin-bottom: 24px; flex-wrap: wrap;
  font-family: "Roboto", "Segoe UI", Arial, sans-serif;

  input, select {
    width: 196px; max-width: 100%; height: 44px; padding: 0 14px;
    border: 1px solid #E0E3EA; border-radius: 8px; background: #fff;
    font-family: inherit; font-size: 13px; font-weight: 400; color: #666F82; outline: none;
    &::placeholder { color: #9EA6B6; }
    &:focus { border-color: #132B59; }
  }

  button {
    height: 44px; border-radius: 8px; cursor: pointer; font-family: inherit; font-size: 13px; font-weight: 500;
    &.primary { min-width: 108px; padding: 0 18px; border: 0; background: #132B59; color: #fff;
      &:hover { background: #0e2147; }
    }
    &.ghost { padding: 0 18px; background: #fff; color: #666F82; border: 1px solid #E0E3EA;
      &:hover { background: #f7f9fc; }
    }
  }

  @media (max-width: 575px) {
    input, select, button { width: 100%; }
  }
}

.status { background: #fff; padding: 1.5rem; border-radius: 8px; text-align: center; color: #666; }

.grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 349px)); gap: 20px; justify-content: center; }

.card {
  width: 100%;
  max-width: 349px;
  min-height: 210px;
  background: #fff;
  border: 1px solid #E0E3EA;
  border-radius: 12px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  cursor: pointer;
  font-family: "Roboto", "Segoe UI", Arial, sans-serif;
  transition: box-shadow 0.12s ease, border-color 0.12s ease, transform 0.12s ease;
  &:hover { box-shadow: 0 8px 22px rgba(16,24,40,0.08); border-color: #c9d3e3; transform: translateY(-2px); }

  .card-head { display: flex; align-items: center; gap: 12px; margin-bottom: 14px;
    .avatar {
      width: 48px; height: 48px; flex-shrink: 0; border-radius: 50%;
      color: #fff; display: flex; align-items: center; justify-content: center;
      font-weight: 600; font-size: 15px; letter-spacing: 0.5px;
    }
    .identity { min-width: 0;
      h3 {
        margin: 0; color: #132B59; font-size: 15px; font-weight: 600; line-height: 1.1;
        white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
      }
      .meta { margin: 7px 0 0; color: #666F82; font-size: 12px; font-weight: 400; line-height: 1; }
    }
  }

  .chips { display: flex; gap: 6px; flex-wrap: wrap; margin-bottom: 10px; min-height: 22px;
    .chip {
      display: inline-flex; align-items: center; height: 22px; padding: 0 10px;
      border-radius: 11px; font-size: 11px; font-weight: 600; white-space: nowrap;
      &--navy   { background: #294C8E1F; color: #132B59; }
      &--purple { background: #894FE51F; color: #894FE5; }
      &--green  { background: #10B8811F; color: #10B881; }
      &--orange { background: #F39C121F; color: #F39C12; }
      &--red    { background: #EF44441F; color: #EF4444; }
    }
  }

  .tags { display: flex; gap: 6px; flex-wrap: wrap; margin-bottom: 12px;
    .tag {
      display: inline-flex; align-items: center; height: 26px; padding: 0 10px;
      border-radius: 6px; font-size: 12px; font-weight: 500; white-space: nowrap;
      &--filled  { background: #132B59; color: #fff; }
      &--outline { background: #fff; border: 1px solid #E0E3EA; color: #666F82; }
    }
  }

  .apply-btn {
    margin-top: auto; width: 100%; height: 38px; border: 0; border-radius: 8px;
    background: #294C8E; color: #fff; font-family: inherit; font-size: 14px; font-weight: 600;
    cursor: pointer; transition: background 0.12s ease;
    &:hover { background: #213e75; }
  }
}

.pager { display: flex; gap: 0.3rem; margin-top: 1.5rem; flex-wrap: wrap; align-items: center;
  button { min-width: 36px; padding: 0.4rem 0.65rem; background: #fff; border: 1px solid #d0d5dd; border-radius: 5px; font: inherit; font-size: 0.85rem; color: #475569; cursor: pointer;
    &:hover:not(:disabled):not(.active) { background: #f7f9fc; border-color: #94a3b8; }
    &.active { background: #2a5298; color: #fff; border-color: #2a5298; }
    &:disabled { opacity: 0.4; cursor: not-allowed; }
  }
  .total { margin-left: auto; font-size: 0.85rem; color: #64748b; }
}
</style>
